/**
 * usePhotoCapture — unified camera/photo picker for both web and the
 * Capacitor native shell.
 *
 *   const { capture } = usePhotoCapture();
 *   const dataUrl = await capture({ source: 'CAMERA' | 'PHOTOS' | 'PROMPT' });
 *
 * Behaviour:
 *   • Native (Capacitor Android/iOS) → uses @capacitor/camera. The user
 *     is prompted (or sent directly to the rear camera) and the result
 *     is converted to a base64 data URL so it slots straight into the
 *     existing photo / signature DB schema.
 *   • Web                            → falls back to a standard
 *     <input type="file"> with `capture="environment"` so mobile
 *     browsers still open the rear camera. Desktop browsers show the
 *     normal file picker.
 *
 * Returns null if the user cancels.
 */
import { useCallback } from 'react';
import { isNativeApp } from '../native/capacitorBootstrap';

const MAX_BASE64_MB = 4; // 4 MB cap per photo — keep DB documents lean

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function pickFileViaInput({ source = 'PROMPT' } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    // `capture="environment"` is a hint to mobile browsers to open the
    // rear camera directly. Desktop browsers ignore it.
    if (source === 'CAMERA') input.setAttribute('capture', 'environment');
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      if (f.size > MAX_BASE64_MB * 1024 * 1024) {
        alert(`Photo must be smaller than ${MAX_BASE64_MB} MB. This one is ${(f.size / 1024 / 1024).toFixed(1)} MB.`);
        return resolve(null);
      }
      resolve(await fileToDataUrl(f));
    };
    input.click();
  });
}

async function captureNative({ source = 'PROMPT' } = {}) {
  // Dynamic-import so the bundle doesn't carry the plugin on web-only loads.
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  const sourceMap = {
    CAMERA: CameraSource.Camera,
    PHOTOS: CameraSource.Photos,
    PROMPT: CameraSource.Prompt,
  };
  try {
    const photo = await Camera.getPhoto({
      quality: 70,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,  // returns "data:image/jpeg;base64,…"
      source: sourceMap[source] ?? CameraSource.Prompt,
      saveToGallery: false,
      // Reasonable max so we don't store gigantic photos in MongoDB.
      width: 1600,
    });
    return photo?.dataUrl || null;
  } catch (e) {
    // User cancelled or denied permission — return null silently.
    const msg = String(e?.message || '');
    if (/cancel/i.test(msg) || /denied/i.test(msg)) return null;
    console.error('[photoCapture] native error:', e);
    return null;
  }
}

export function usePhotoCapture() {
  const capture = useCallback(async (opts = {}) => {
    if (isNativeApp()) {
      return await captureNative(opts);
    }
    return await pickFileViaInput(opts);
  }, []);

  return { capture };
}

export default usePhotoCapture;
